// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Ctx } from '../main.ts';
import type { Seat } from '../data.ts';
import { pendulum, seatsFlippedBy } from '../analysis.ts';
import { GROUP_META } from '../parties.ts';
import { fmtSigned, esc, escTip, fullName } from '../utils/format.ts';
import { term } from '../glossary.ts';

const MAXBAR = 28; // margins above this share the full bar width

function bandOf(m: number): string {
  if (m < 6) return 'Marginal';
  if (m < 10) return 'Safe';
  return 'Very safe';
}

function rowHtml(s: Seat, side: 'coa' | 'alp'): string {
  const w = (Math.min(s.tppMargin, MAXBAR) / MAXBAR) * 100;
  const col = GROUP_META[s.winnerGroup].color;
  const held = s.member.group === (side === 'alp' ? 'ALP' : 'COA');
  const heldNote = held ? '' : ` · held by ${GROUP_META[s.member.group].label}`;
  const swingLine = s.tppSwingToALP == null ? 'Swing: no 2022 comparison' : `Swing ${fmtSigned(s.tppSwingToALP)} to Labor`;
  const tip = `${s.name} (${s.state})\n${fullName(s.member.given, s.member.surname)} — ${GROUP_META[s.member.group].label}\nMargin ${s.tppMargin.toFixed(1)}% (${bandOf(s.tppMargin)})\n${swingLine}${heldNote}`;
  return `<button class="pend-row ${side}" data-seat="${esc(s.name)}" data-margin="${s.tppMargin.toFixed(3)}" data-alp="${s.tppALPPct.toFixed(3)}" data-tip="${escTip(tip)}" aria-label="${esc(s.name)}, margin ${s.tppMargin.toFixed(1)} percent">
    <span class="pr-name">${esc(s.name)}${held ? '' : ` <span class="pr-flag" style="background:${GROUP_META[s.member.group].color}"></span>`}</span>
    <span class="pr-margin">${s.tppMargin.toFixed(1)}</span>
    <span class="pr-bar"><span style="width:${w}%;background:${col}"></span></span>
  </button>`;
}

export function renderPendulum(root: HTMLElement, ctx: Ctx): void {
  const { alp, coa } = pendulum(ctx.data.seats);

  root.innerHTML = `
    <div class="view-head">
      <h2>The pendulum</h2>
      <p class="subtitle">Every seat lined up by its ${term('margin')}, most marginal at the top. The ${term('pendulum', 'Mackerras pendulum')} shows what a uniform swing would flip — drag the slider to try one. Bars are coloured by who actually holds the seat.</p>
    </div>

    <div class="panel">
      <div class="panel-title">Uniform swing simulator</div>
      <div class="panel-sub">If the same swing happened in every seat, who would win? (A real election never swings uniformly — this is the classic thought experiment.)</div>
      <div class="controls" style="align-items:center;gap:var(--sp-lg)">
        <label for="swing" style="font-weight:600">Swing:</label>
        <input id="swing" type="range" min="-10" max="10" step="0.1" value="0" style="flex:1;min-width:160px" aria-label="Uniform swing to Labor" />
        <span id="swing-val" class="mono" style="font-weight:800;font-size:var(--fs-lg);min-width:9ch;text-align:right"></span>
      </div>
      <div id="sim-tally" class="stat-grid" style="grid-template-columns:repeat(auto-fit,minmax(min(150px,100%),1fr))"></div>
      <div id="sim-flips" class="note"></div>
    </div>

    <div class="panel">
      <div class="legend" style="margin-bottom:var(--sp-md)">
        <span class="li"><span class="sw" style="background:${GROUP_META.ALP.color}"></span>Labor-held</span>
        <span class="li"><span class="sw" style="background:${GROUP_META.COA.color}"></span>Coalition-held</span>
        <span class="li"><span class="sw" style="background:${GROUP_META.IND.color}"></span>Independent</span>
        <span class="li"><span class="sw" style="background:${GROUP_META.GRN.color}"></span>Green</span>
        <span class="li" style="color:var(--text-tertiary)">A dot means the seat leans one way on the two-party line but is held by someone else.</span>
      </div>
      <div class="pendulum">
        <div class="pend-col">
          <div class="pend-head" style="color:${GROUP_META.COA.color}">Coalition-leaning · ${coa.seats.length} seats</div>
          ${coa.seats.map((s) => rowHtml(s, 'coa')).join('')}
        </div>
        <div class="pend-col">
          <div class="pend-head" style="color:${GROUP_META.ALP.color}">Labor-leaning · ${alp.seats.length} seats</div>
          ${alp.seats.map((s) => rowHtml(s, 'alp')).join('')}
        </div>
      </div>
    </div>`;

  root.querySelectorAll<HTMLButtonElement>('.pend-row').forEach((b) => {
    b.addEventListener('click', () => ctx.openSeat(b.dataset.seat!));
  });

  const slider = root.querySelector('#swing') as HTMLInputElement;
  const valEl = root.querySelector('#swing-val')!;
  const tally = root.querySelector('#sim-tally')!;
  const flipsEl = root.querySelector('#sim-flips')!;

  const update = () => {
    const sw = parseFloat(slider.value);
    valEl.textContent = sw === 0 ? 'none' : `${fmtSigned(sw)} to ${sw > 0 ? 'ALP' : 'L/NP'}`;
    // Recompute notional two-party holders after a uniform swing.
    let alpSeats = 0;
    let coaSeats = 0;
    for (const s of ctx.data.seats) {
      if (s.tppALPPct + sw >= 50) alpSeats++;
      else coaSeats++;
    }
    tally.innerHTML = `
      <div class="stat" style="border-left-color:var(--alp)"><div class="k">Labor (notional TPP)</div><div class="v" style="color:var(--alp)">${alpSeats}</div><div class="d">${alpSeats >= 76 ? 'majority' : 'short of 76'}</div></div>
      <div class="stat" style="border-left-color:var(--coa)"><div class="k">Coalition (notional TPP)</div><div class="v" style="color:var(--coa)">${coaSeats}</div><div class="d">${coaSeats >= 76 ? 'majority' : 'short of 76'}</div></div>`;
    const flips = seatsFlippedBy(ctx.data.seats, sw).sort((a, b) => a.tppMargin - b.tppMargin);
    const flipNames = new Set(flips.map((s) => s.name));
    root.querySelectorAll<HTMLElement>('.pend-row').forEach((el) => {
      el.classList.toggle('flipped', flipNames.has(el.dataset.seat!));
    });
    flipsEl.innerHTML = sw === 0
      ? 'At no swing, the result stands as it was. Drag to see the battleground.'
      : `<strong>${flips.length}</strong> seat${flips.length === 1 ? '' : 's'} would change hands on the two-party line: ${flips.slice(0, 14).map((s) => `<a href="#v=pendulum&seat=${encodeURIComponent(s.name)}">${esc(s.name)}</a>`).join(', ')}${flips.length > 14 ? ` +${flips.length - 14} more` : ''}.`;
  };
  slider.addEventListener('input', update);
  update();
}
